import type { Db } from "./client";
import { getUserId } from "./client";
import {
  plantCreateSchema, plantUpdateSchema,
  type PlantCreateInput, type PlantUpdateInput,
} from "./schemas";
import { ValidationError, NotFoundError, throwIfPgError } from "./errors";
import type { PlantRow, PlantInsert } from "@garden/types/db";

export async function createPlant(
  client: Db,
  input: PlantCreateInput
): Promise<PlantRow> {
  const parsed = plantCreateSchema.safeParse(input);
  if (!parsed.success) {
    throw new ValidationError("invalid plant input", parsed.error);
  }
  const userId = await getUserId(client);
  const { data, error } = await client
    .from("plant")
    .insert({ ...parsed.data, user_id: userId } as PlantInsert) // `as` cast sanctioned at Supabase boundary per spec §5 (unknown→Json for jsonb fields)
    .select("*")
    .single();
  throwIfPgError(error);
  return data as PlantRow; // `as` cast sanctioned at Supabase boundary per spec §5
}

export async function listPlants(
  client: Db,
  gardenId?: string,
): Promise<PlantRow[]> {
  let q = client.from("plant").select("*");
  if (gardenId) q = q.eq("garden_id", gardenId);
  const { data, error } = await q.order("created_at", { ascending: false });
  throwIfPgError(error);
  return (data ?? []) as PlantRow[];
}

export async function listPlantsByLocation(
  client: Db,
  locationId: string
): Promise<PlantRow[]> {
  const { data, error } = await client
    .from("plant")
    .select("*")
    .eq("location_id", locationId)
    .order("created_at", { ascending: false });
  throwIfPgError(error);
  return (data ?? []) as PlantRow[];
}

export async function getPlant(client: Db, id: string): Promise<PlantRow> {
  const { data, error } = await client
    .from("plant")
    .select("*")
    .eq("id", id)
    .maybeSingle();
  throwIfPgError(error);
  if (!data) throw new NotFoundError("plant", id);
  return data as PlantRow; // `as` cast sanctioned at Supabase boundary per spec §5
}

export async function updatePlant(
  client: Db,
  id: string,
  patch: PlantUpdateInput
): Promise<PlantRow> {
  const parsed = plantUpdateSchema.safeParse(patch);
  if (!parsed.success) {
    throw new ValidationError("invalid plant patch", parsed.error);
  }
  const { data, error } = await client
    .from("plant")
    .update(parsed.data as Partial<Omit<PlantInsert, "user_id">>) // `as` cast sanctioned at Supabase boundary per spec §5 (unknown→Json for jsonb fields)
    .eq("id", id)
    .select("*")
    .maybeSingle();
  throwIfPgError(error);
  if (!data) throw new NotFoundError("plant", id);
  return data as PlantRow; // `as` cast sanctioned at Supabase boundary per spec §5
}

export async function deletePlant(client: Db, id: string): Promise<void> {
  const { error } = await client.from("plant").delete().eq("id", id);
  throwIfPgError(error);
}
